import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Check, Video, Flame, Play, Pause, X, Maximize2, Send, Ban, PauseCircle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loading } from "@/components/shared";
import api, { API } from "@/lib/api";

const ORIGIN = API.replace(/\/api$/, "");

function CopyBtn({ text, label = "Copy", testid, className = "" }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text || ""); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); };
  return (
    <button onClick={copy} data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 ${className}`}>
      {done ? <Check className="h-3.5 w-3.5 text-emerald" /> : <Copy className="h-3.5 w-3.5" />} {label}
    </button>
  );
}

function Teleprompter({ text, onClose }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const wakeRef = useRef(null);
  useEffect(() => {
    (async () => { try { if ("wakeLock" in navigator) wakeRef.current = await navigator.wakeLock.request("screen"); } catch {} })();
    return () => { try { wakeRef.current?.release(); } catch {} };
  }, []);
  useEffect(() => {
    if (!playing) return;
    const iv = setInterval(() => { if (ref.current) ref.current.scrollTop += 1; }, 40);
    return () => clearInterval(iv);
  }, [playing]);
  return (
    <div className="fixed inset-0 z-[200] bg-black text-white" data-testid="teleprompter">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <button onClick={() => setPlaying((p) => !p)} data-testid="tele-playpause" className="inline-flex items-center gap-2 text-sm">{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />} {playing ? "Pause" : "Play"}</button>
        <span className="text-xs uppercase tracking-widest text-white/50">Tap text to pause · scroll to seek</span>
        <button onClick={onClose} data-testid="tele-close" className="text-white/70 hover:text-white"><X className="h-6 w-6" /></button>
      </div>
      <div ref={ref} onClick={() => setPlaying((p) => !p)} className="h-[calc(100vh-52px)] overflow-y-auto px-6 py-24">
        <p className="mx-auto max-w-2xl whitespace-pre-wrap text-center text-3xl font-semibold leading-[1.6] tracking-tight sm:text-4xl">{text}</p>
        <div className="h-[60vh]" />
      </div>
    </div>
  );
}

function TodayShoot() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["desk-today"], queryFn: async () => (await api.get("/blog/desk/today-shoot")).data });
  const [tele, setTele] = useState(false);
  if (isLoading) return <Loading />;
  const post = data?.post;
  if (!post) return <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400" data-testid="today-shoot-empty">No post to film yet. Ingest a story and it'll appear here.</div>;
  const v = post.social?.video || {};
  const soc = post.social || {};
  const alias = `${ORIGIN}/d/${post.slug}`;
  const toggleShot = async () => {
    try { await api.put(`/blog/desk/${post.id}/shot`, { shot: !post.shot }); qc.invalidateQueries({ queryKey: ["desk-today"] }); toast.success(!post.shot ? "Marked as shot" : "Unmarked"); }
    catch { toast.error("Failed"); }
  };
  const xBlock = (soc.x || []).join("\n\n");
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-navy/15 bg-white p-5 shadow-sm" data-testid="today-shoot-card">
      {tele && <Teleprompter text={v.script || ""} onClose={() => setTele(false)} />}
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-600" data-testid="shoot-streak"><Flame className="h-3.5 w-3.5" /> {data.streak}-day streak</span>
        <button onClick={toggleShot} data-testid="shot-it-toggle" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${post.shot ? "bg-emerald text-white" : "border border-slate-200 text-slate-500"}`}>
          <Check className="h-3.5 w-3.5" /> {post.shot ? "Shot it" : "Mark shot"}
        </button>
      </div>

      <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">The hook</p>
      <p className="mt-1 font-heading text-2xl font-bold leading-tight text-navy" data-testid="shoot-hook">{v.hook || "—"}</p>
      <div className="mt-2 flex gap-2"><CopyBtn text={v.hook} label="Copy hook" testid="copy-hook" /></div>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Script</p>
        <button onClick={() => setTele(true)} data-testid="open-teleprompter" className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white"><Maximize2 className="h-3.5 w-3.5" /> Teleprompter</button>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-[17px] leading-[1.8] text-slate-800" data-testid="shoot-script">{v.script || "—"}</p>
      <div className="mt-2"><CopyBtn text={v.script} label="Copy script" testid="copy-script" /></div>

      {(v.on_screen_text || []).length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">On-screen text</p>
          <ol className="mt-2 space-y-1.5">
            {v.on_screen_text.map((t, i) => (
              <li key={i} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid={`ost-${i}`}>
                <span><span className="mr-2 font-mono text-xs text-slate-400">{i + 1}</span>{t}</span>
                <CopyBtn text={t} label="" testid={`copy-ost-${i}`} className="!px-2" />
              </li>
            ))}
          </ol>
        </div>
      )}

      {(v.b_roll || []).length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">B-roll checklist</p>
          <ul className="mt-2 space-y-1.5">
            {v.b_roll.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`broll-${i}`}><input type="checkbox" className="mt-1" /> {b}</li>)}
          </ul>
        </div>
      )}

      {v.cta_line && <div className="mt-5"><p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Closing line</p><p className="mt-1 text-sm text-slate-700">{v.cta_line}</p><div className="mt-1"><CopyBtn text={v.cta_line} label="Copy CTA" testid="copy-cta" /></div></div>}

      <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="shoot-alias">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Read this URL on camera</p>
        <div className="mt-1 flex items-center justify-between gap-2"><code className="text-sm text-navy">{ORIGIN.replace(/^https?:\/\//, "")}/d/{post.slug}</code><CopyBtn text={alias} label="" testid="copy-alias" className="!px-2" /></div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-2 border-t border-slate-100 pt-4">
        <CopyBtn text={soc.linkedin} label="LinkedIn post" testid="copy-linkedin" />
        <CopyBtn text={xBlock} label="X thread (all)" testid="copy-x-all" />
        <CopyBtn text={soc.instagram?.caption} label="IG caption" testid="copy-ig-caption" />
        <CopyBtn text={(soc.instagram?.hashtags || []).join(" ")} label="IG hashtags" testid="copy-ig-tags" />
      </div>
      {(soc.x || []).length > 0 && (
        <div className="mt-3 space-y-1.5">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">X thread — individual</p>
          {soc.x.map((t, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid={`x-post-${i}`}>
              <span className="line-clamp-2">{t}</span><CopyBtn text={t} label="" testid={`copy-x-${i}`} className="!px-2" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditDialog({ post, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(post);
  useEffect(() => { setF(post); }, [post]);
  const save = async () => {
    try {
      const payload = {
        title: f.title, dek: f.dek, company: f.company, region: f.region, sector: f.sector,
        story: f.story, why_it_happened: f.why_it_happened, operator_take: f.operator_take,
        question: f.question, video_url: f.video_url || "",
      };
      await api.put(`/blog/desk/${post.id}`, payload);
      toast.success("Saved"); setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const T = ({ k, label, rows }) => rows
    ? <div><Label className="text-xs">{label}</Label><Textarea rows={rows} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`edit-${k}`} /></div>
    : <div><Label className="text-xs">{label}</Label><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`edit-${k}`} /></div>;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`edit-${post.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>Edit post</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <T k="title" label="Title" />
          <T k="dek" label="Dek" />
          <div className="grid grid-cols-3 gap-3"><T k="company" label="Company" /><T k="region" label="Region" /><T k="sector" label="Sector" /></div>
          <T k="story" label="Story" rows={5} />
          <T k="why_it_happened" label="Why it happened" rows={4} />
          <T k="operator_take" label="Operator take" rows={5} />
          <T k="question" label="Question" rows={2} />
          <T k="video_url" label="Video URL (YouTube / Instagram)" />
        </div>
        <DialogFooter><Button onClick={save} className="bg-emerald text-white hover:bg-emerald-700" data-testid="edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeskList() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("queued");
  const { data: counts } = useQuery({ queryKey: ["desk-counts"], queryFn: async () => (await api.get("/blog/desk/counts")).data });
  const { data, isLoading } = useQuery({ queryKey: ["desk-list", status], queryFn: async () => (await api.get(`/blog/desk?status=${status}`)).data });
  const refresh = () => ["desk-list", "desk-counts", "desk-today", "blog", "blog-facets"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const act = async (id, action) => {
    try { await api.post(`/blog/desk/${id}/${action}`); toast.success(action); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const TABS = [["queued", "Queued"], ["published", "Published"], ["held", "Held"], ["rejected", "Rejected"]];
  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-1">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`desk-tab-${v}`}
            className={`flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition-colors ${status === v ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {l} <span className={`rounded px-1.5 text-xs tabular-nums ${status === v ? "bg-white/20" : "bg-slate-200 text-slate-600"}`}>{counts?.[v] ?? 0}</span>
          </button>
        ))}
      </div>
      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center text-slate-400">No {status} posts.</div>
      ) : (
        <div className="space-y-3">
          {(data || []).map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`desk-row-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-navy">{p.title}</h3>
                  <p className="mt-1 text-sm text-slate-500">{p.company} · {p.region} · {p.sector}</p>
                  {p.held_reason && status === "held" && <p className="mt-1 text-xs font-semibold text-orange-600" data-testid={`held-reason-${p.id}`}>{p.held_reason}</p>}
                  <p className="mt-1 text-xs text-slate-400">/d/{p.slug}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <EditDialog post={p} onSaved={refresh} />
                  {status !== "published" && <Button size="sm" onClick={() => act(p.id, "publish")} className="bg-emerald text-white hover:bg-emerald-700" data-testid={`publish-${p.id}`}><Send className="mr-1.5 h-3.5 w-3.5" /> Publish now</Button>}
                  {status !== "held" && status !== "published" && <Button size="sm" variant="outline" onClick={() => act(p.id, "hold")} data-testid={`hold-${p.id}`}><PauseCircle className="mr-1.5 h-3.5 w-3.5" /> Hold</Button>}
                  {status !== "rejected" && <Button size="sm" variant="outline" onClick={() => act(p.id, "reject")} className="text-red-600 hover:bg-red-50" data-testid={`reject-${p.id}`}><Ban className="mr-1.5 h-3.5 w-3.5" /> Reject</Button>}
                </div>
              </div>
              <p className="mt-3 line-clamp-2 text-sm text-slate-600">{p.dek || p.excerpt}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DeskAdmin() {
  return (
    <div data-testid="desk-admin">
      <div className="mb-6">
        <h2 className="mb-1 flex items-center gap-2 font-heading text-xl font-semibold text-navy"><Video className="h-5 w-5 text-emerald" /> Today's shoot</h2>
        <p className="text-sm text-slate-500">Open this on your phone before filming. Read the script off the teleprompter.</p>
      </div>
      <TodayShoot />
      <div className="mt-10">
        <h2 className="mb-4 font-heading text-xl font-semibold text-navy">The Desk — pipeline</h2>
        <DeskList />
      </div>
    </div>
  );
}
